import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
    BehaviorSubject, Observable, distinctUntilChanged,
    filter, first, map, shareReplay, switchMap, withLatestFrom,
} from 'rxjs';

import { SastreService } from '../sastre.service';

import { SastreButtonService } from './domain/sastre-button.service';
import { SastreButtonApiRepository } from './infrastructure/sastre-button.api.repository';

import { ProductPublicModels } from '@features/products/product';
import { SidebarCustomComponents, SidebarPublicPort } from '@features/sidebar';
import { SidebarView } from '@features/sidebar/domain/models/sidebar.models';

@Component({
    selector: 'sastre-button',
    templateUrl: './sastre-button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,

    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
    ],
    providers: [
        SastreButtonApiRepository,
        SastreButtonService,
    ],
})
export class SastreButtonComponent implements OnInit, OnChanges {
    @Input() public product: ProductPublicModels.PublicProduct;

    constructor(
        private readonly store: Store,
        private readonly service: SastreButtonService
    ) { }

    private readonly _product$: BehaviorSubject<ProductPublicModels.PublicProduct> = new BehaviorSubject(null);

    private readonly productId$ = this._product$.asObservable().pipe(
        filter((product) => !!product),
        map((product) => SastreService.getProductId(product)),
        distinctUntilChanged(),
    );

    private readonly _recommendedSize$: BehaviorSubject<string> = new BehaviorSubject(null);

    public recommendedSize$: Observable<string> = this._recommendedSize$.asObservable();

    public activated$: Observable<boolean> = this.productId$.pipe(
        switchMap((id) => this.service.isActivated(id)),
        shareReplay(1),
    );

    public loaded$: Observable<boolean> = this.activated$.pipe(
        filter(activated => !!activated),
        switchMap(() => this.recommendedSize$),
        map(() => true),
    );

    public ngOnInit(): void {
        this._product$.next(this.product);
        this.checkActivated();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.product) {
            this._product$.next(changes.product.currentValue);
        }
    }

    private checkActivated(): void {
        this.activated$.pipe(
            filter(activated => !!activated),
            withLatestFrom(this.productId$),
            switchMap(([_, id]) => this.service.recommendation(id)),
            map((recommendation) => recommendation?.sizeResult || null),
            first(),
        ).subscribe((recommendedSize) => {
            this._recommendedSize$.next(recommendedSize);
        });
    }

    public show() {
        SidebarPublicPort(this.store).openSidebarCustom({
            component: SidebarCustomComponents.SASTRE,
            initializeFunction: undefined,
            data: { product: this.product },
            showBack: false,
            view: SidebarView.CUSTOM_NEW,
        });
    }
}
